import { Link } from "react-router-dom";
import { SignInButton, SignUpButton } from "@clerk/clerk-react";
import { useEffect, useRef } from "react";

// ════════════════════════════════════════════
// SVG ICONS
// ════════════════════════════════════════════

const IconDocument = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0d9488"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const IconShield = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0d9488"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const IconChat = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0d9488"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    <path d="M8 10h8" />
    <path d="M8 14h4" />
  </svg>
);

const IconChart = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0d9488"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 21H4.6c-.6 0-.9 0-1.1-.1a1 1 0 01-.4-.4C3 20.3 3 20 3 19.4V3" />
    <path d="M7 14l4-4 4 4 6-6" />
  </svg>
);

const IconFunnel = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0d9488"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 2h6l3 7H6L9 2z" />
    <path d="M12 9v13" />
    <path d="M8 22h8" />
    <circle cx="12" cy="5" r="1" fill="#0d9488" stroke="none" />
  </svg>
);

const IconLock = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0d9488"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
    <circle cx="12" cy="16" r="1" fill="#0d9488" stroke="none" />
  </svg>
);

const IconPhone = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth={2}
    strokeLinecap="round"
  >
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
  </svg>
);

const IconCheck = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M3 6.5l2 2 4-4"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
    />
  </svg>
);

// ════════════════════════════════════════════
// FADE-UP OBSERVER HOOK
// ════════════════════════════════════════════

function useFadeUp() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    const els = ref.current.querySelectorAll(".fade-up");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return ref;
}

// ════════════════════════════════════════════
// FEATURE DATA
// ════════════════════════════════════════════

const FEATURES = [
  // Top row — live now
  {
    icon: <IconDocument />,
    title: "AI-Generated PA Letters",
    desc: "Enter your clinical scenario and get a fully structured prior authorization letter tailored to the insurer's specific criteria — not a generic template, but a targeted clinical argument.",
  },
  {
    icon: <IconChart />,
    title: "Insurer Intelligence",
    desc: "Know which criteria system each insurer uses before the call starts. 100+ insurer profiles with mapped clinical criteria, so you speak their language from word one.",
  },
  {
    icon: <IconLock />,
    title: "De-Identified by Design",
    desc: "Patient data is scrubbed on your device before anything reaches our servers. Not as a feature — as the architecture. No PHI exposure, period.",
  },
  // Bottom row — coming soon
  {
    icon: <IconShield />,
    title: "Call Shield",
    desc: "Never miss a peer-to-peer call again. PeerPrep auto-answers the insurer, holds the reviewer on the line, and alerts you via push notification — then bridges you in.",
    comingSoon: true,
  },
  {
    icon: <IconChat />,
    title: "Live Rebuttal Mode",
    desc: "During your P2P call, real-time transcription identifies the reviewer's objections and surfaces matching clinical rebuttals from our curated library — instantly.",
    comingSoon: true,
  },
  {
    icon: <IconFunnel />,
    title: "P2P Prep Guides",
    desc: "Structured preparation guides that walk you through likely objections, required clinical evidence, and the strongest arguments for your specific case.",
    comingSoon: true,
  },
];

// ════════════════════════════════════════════
// INLINE STYLES (for elements that need custom
// CSS beyond what Tailwind provides easily)
// ════════════════════════════════════════════

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');

  .landing-page {
    font-family: 'DM Sans', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
  }
  .landing-page .font-display {
    font-family: 'DM Serif Display', serif;
  }
  .landing-page .gradient-text {
    background: linear-gradient(120deg, #0d9488 0%, #2dd4bf 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  /* Fade-up animation */
  .landing-page .fade-up {
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.7s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .landing-page .fade-up.visible {
    opacity: 1;
    transform: translateY(0);
  }

  /* Mock float animation */
  @keyframes mockFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
  }
  .landing-page .mock-float {
    animation: mockFloat 6s ease-in-out infinite;
  }

  /* Call Shield pulse */
  @keyframes csPulse {
    0%, 100% { border-color: rgba(255,255,255,0.12); }
    50% { border-color: rgba(13,148,136,0.5); }
  }
  .landing-page .cs-pulse {
    animation: csPulse 3s ease-in-out infinite;
  }

  /* Phone ring */
  @keyframes csRing {
    0%, 100% { transform: rotate(0deg); }
    10% { transform: rotate(12deg); }
    20% { transform: rotate(-12deg); }
    30% { transform: rotate(6deg); }
    40% { transform: rotate(0deg); }
  }
  .landing-page .cs-ring {
    animation: csRing 1.5s ease-in-out infinite;
  }

  /* Feature card top bar on hover */
  .landing-page .feature-card::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, #0d9488, #2dd4bf);
    opacity: 0;
    transition: opacity 0.3s;
  }
  .landing-page .feature-card:hover::after {
    opacity: 1;
  }

`;

// ════════════════════════════════════════════
// NAV
// ════════════════════════════════════════════

function Nav() {
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const handler = () => {
      navRef.current?.classList.toggle("shadow-md", window.scrollY > 20);
    };
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <nav
      ref={navRef}
      className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/85 backdrop-blur-xl border-b border-white/[0.06] transition-shadow"
    >
      <div className="max-w-[1320px] mx-auto px-8 py-3.5 flex items-center justify-between">
        <a
          href="#"
          className="font-display text-2xl text-white no-underline tracking-tight"
        >
          Peer<span className="text-teal-400">Prep</span>
        </a>
        <div className="flex items-center gap-8">
          <a
            href="#features"
            className="hidden sm:inline text-sm font-medium text-zinc-400 hover:text-white no-underline transition-colors"
          >
            Features
          </a>
          <a
            href="#pricing"
            className="hidden sm:inline text-sm font-medium text-zinc-400 hover:text-white no-underline transition-colors"
          >
            Pricing
          </a>
          <SignUpButton mode="redirect">
            <button className="inline-flex items-center gap-2 bg-teal-600 text-white px-5 py-2.5 rounded-lg text-sm font-semibold shadow-md shadow-teal-600/25 hover:bg-teal-700 hover:shadow-lg hover:shadow-teal-600/30 transition-all hover:-translate-y-px cursor-pointer">
              Get Started Free →
            </button>
          </SignUpButton>
        </div>
      </div>
    </nav>
  );
}

// ════════════════════════════════════════════
// HERO
// ════════════════════════════════════════════

function Hero() {
  return (
    <section className="bg-zinc-950 relative overflow-hidden">
      {/* Subtle radial glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_15%_50%,rgba(13,148,136,0.05)_0%,transparent_55%),radial-gradient(ellipse_at_80%_20%,rgba(45,212,191,0.025)_0%,transparent_50%)] pointer-events-none" />

      <div className="pt-36 pb-20 px-12 lg:px-16 max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
        <div className="fade-up">
          {/* Built by a physician badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-400/[0.08] border border-teal-400/[0.18] mb-8">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#2dd4bf"
              strokeWidth={2.5}
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span className="text-[13px] font-semibold text-teal-400">
              Built by a physician
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-[3.4rem] leading-[1.08] tracking-tight mb-6 text-white">
            Stop writing PA letters{" "}
            <span className="gradient-text whitespace-nowrap">
              in the dark.
            </span>
          </h1>
          <p className="text-lg text-zinc-400 max-w-[500px] mb-9 leading-relaxed">
            Insurers evaluate your claims using clinical criteria you can't see.
            PeerPrep gives you the evidence, guidelines, and rebuttals to fight
            back — in a letter generated in under two minutes.
          </p>

          {/* CTA */}
          <div className="max-w-[500px] mb-6">
            <div className="flex gap-3 flex-wrap mb-4">
              <Link
                to="/try?mode=prior-auth"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg text-sm font-semibold shadow-md shadow-teal-600/25 hover:bg-teal-700 hover:shadow-lg hover:shadow-teal-600/30 transition-all hover:-translate-y-px no-underline"
              >
                Generate Free Prior Auth Letter →
              </Link>
              <a
                href="#how"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium border border-white/[0.1] text-zinc-400 hover:border-white/[0.2] hover:text-white transition-all no-underline"
              >
                See How It Works
              </a>
            </div>
            <div className="flex gap-5 flex-wrap">
              {[
                "No account required",
                "No credit card",
                "Designed for HIPAA",
              ].map((item) => (
                <span
                  key={item}
                  className="flex items-center gap-1.5 text-xs text-zinc-500"
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="8" fill="#0d9488" />
                    <path
                      d="M5 8.5L7 10.5L11 6"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Hero mock — PA Letter preview (original, unchanged) */}
        <div className="fade-up relative flex justify-center lg:order-none order-first">
          {/* Teal glow behind card */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-[400px] h-[400px] bg-teal-500/[0.07] rounded-full blur-[80px]" />
          </div>
          <div className="mock-float w-full max-w-[480px] bg-white rounded-2xl border border-zinc-200 shadow-[0_2px_8px_rgba(0,0,0,0.25),0_16px_48px_rgba(0,0,0,0.35),0_0_80px_rgba(13,148,136,0.12)] overflow-hidden relative">
            {/* App chrome header */}
            <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-zinc-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                  />
                </svg>
                <span className="font-semibold text-sm text-zinc-900">
                  Prior Auth Letter
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-teal-500" />
                <span className="text-xs font-medium text-teal-600">Ready</span>
              </div>
            </div>

            {/* Action bar */}
            <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
              {["Copy to Clipboard", "Download PDF", "Edit"].map((action) => (
                <span
                  key={action}
                  className="text-[10px] font-medium text-zinc-500 bg-zinc-100 px-2.5 py-1 rounded-md"
                >
                  {action}
                </span>
              ))}
            </div>

            {/* Letter content */}
            <div
              className="px-6 py-5 relative"
              style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
            >
              <div
                className="space-y-1 text-[11px] text-zinc-500 mb-3"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <div>Lakeview Rheumatology Associates</div>
                <div>4200 Medical Pkwy, Suite 310, Austin, TX 78756</div>
              </div>

              <div
                className="space-y-0.5 text-[11px] text-zinc-700 mb-4"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <div>Medical Director</div>
                <div>Prior Authorization Department</div>
                <div className="font-medium">Blue Cross Blue Shield</div>
              </div>

              <div
                className="text-[11px] text-zinc-700 mb-1"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <span className="font-bold text-zinc-900">RE:</span> Prior
                Authorization Request
              </div>
              <div
                className="text-[11px] text-zinc-700 mb-1"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <span className="font-bold text-zinc-900">
                  Service Requested:
                </span>{" "}
                Rinvoq (upadacitinib)
              </div>
              <div
                className="text-[11px] text-zinc-700 mb-4"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <span className="font-bold text-zinc-900">Diagnosis:</span>{" "}
                Seropositive rheumatoid arthritis
              </div>

              {/* Medical Necessity */}
              <div className="mb-4">
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-900 mb-1 pb-1 border-b border-teal-500/40">
                  MEDICAL NECESSITY
                </div>
                <p
                  className="text-[11px] text-zinc-700 leading-relaxed mt-2"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  This 45-year-old female with 6-year history of seropositive
                  rheumatoid arthritis demonstrates moderate-to-high disease
                  activity (DAS28-CRP 5.1, CDAI 28) with radiographic
                  progression despite sequential failure of three conventional
                  DMARDs…
                </p>
              </div>

              {/* Criteria Met */}
              <div className="mb-2">
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-900 mb-1 pb-1 border-b border-teal-500/40">
                  CRITERIA MET
                </div>
                <div
                  className="mt-2 space-y-1.5 text-[11px] text-zinc-700 leading-relaxed"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  <div>
                    1. ACR 2021 Guideline: JAK inhibitors recommended for
                    inadequate response to conventional DMARDs
                  </div>
                  <div>
                    2. ACR 2021 Table 4: TNF inhibitor use discouraged with
                    family history of demyelinating disease
                  </div>
                </div>
              </div>

              {/* Fade overlay */}
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════
// PROBLEM / STATS
// ════════════════════════════════════════════

function ProblemSection() {
  return (
    <section className="bg-zinc-900 text-white py-20 px-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(13,148,136,0.2)_0%,transparent_50%),radial-gradient(circle_at_80%_20%,rgba(20,184,166,0.12)_0%,transparent_50%)] pointer-events-none" />
      <div className="max-w-[900px] mx-auto relative z-10 text-center fade-up">
        <div className="text-xs font-bold uppercase tracking-[0.12em] text-teal-400 mb-4">
          The Problem
        </div>
        <h2 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight mb-6">
          You're writing a letter to pass a test
          <br />
          you're not allowed to study for.
        </h2>
        <p className="text-lg leading-relaxed text-white/70 max-w-[680px] mx-auto mb-12">
          Insurance reviewers use proprietary criteria systems to deny care.
          Most physicians have never seen these criteria, yet they're expected
          to argue against them in 10‑minute peer-to-peer calls.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-[750px] mx-auto">
          {[
            {
              num: "88%",
              label: "of PA denials are never appealed by physicians",
            },
            { num: "13h", label: "spent per physician per week on prior auth" },
            {
              num: "81%",
              label: "of appealed denials are eventually overturned",
            },
          ].map((s) => (
            <div
              key={s.num}
              className="bg-white/[0.06] border border-white/10 rounded-xl p-8 text-center hover:-translate-y-1 hover:bg-white/10 transition-all"
            >
              <div className="font-display text-5xl text-teal-400 leading-none mb-2">
                {s.num}
              </div>
              <div className="text-sm text-white/60 leading-snug">
                {s.label}
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-white/30 mt-6">
          Sources: AMA 2024 Prior Authorization Physician Survey, KFF Medicare
          Advantage Prior Authorization Data 2024
        </p>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════
// FEATURES
// ════════════════════════════════════════════

function FeaturesSection() {
  const bgClasses = [
    "bg-teal-50",
    "bg-teal-50/50",
    "bg-teal-100/60",
    "bg-teal-50/50",
    "bg-teal-100/60",
    "bg-teal-50/50",
  ];
  return (
    <section className="py-24 px-8 max-w-[1200px] mx-auto" id="features">
      <div className="text-center mb-16 fade-up">
        <div className="text-xs font-bold uppercase tracking-[0.12em] text-teal-600 mb-4">
          What You Get
        </div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-4">
          We leveled the playing field. Your move.
        </h2>
        <p className="text-zinc-500 text-lg max-w-[580px] mx-auto">
          Built by a physician who realized the system was rigged — and decided
          to do something about it.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {FEATURES.map((f, i) => (
          <div
            key={f.title}
            className={`feature-card fade-up relative overflow-hidden bg-white border border-zinc-200 rounded-2xl p-8 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/[0.04] transition-all ${f.comingSoon ? "opacity-75" : ""}`}
          >
            {f.comingSoon && (
              <span className="absolute top-4 right-4 text-[10px] font-semibold uppercase tracking-wider text-teal-600 bg-teal-50 border border-teal-200/60 px-2.5 py-1 rounded-full">
                Coming Soon
              </span>
            )}
            <div
              className={`w-[52px] h-[52px] rounded-xl flex items-center justify-center mb-6 ${bgClasses[i]}`}
            >
              {f.icon}
            </div>
            <h3 className="text-base font-bold text-zinc-900 mb-3">
              {f.title}
            </h3>
            <p className="text-sm text-zinc-500 leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ════════════════════════════════════════════
// HOW IT WORKS
// ════════════════════════════════════════════

function HowItWorks() {
  return (
    <section className="bg-zinc-100 py-24 px-8" id="how">
      <div className="max-w-[900px] mx-auto">
        <div className="text-center fade-up">
          <div className="text-xs font-bold uppercase tracking-[0.12em] text-teal-600 mb-4">
            How It Works
          </div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-6">
            How your letter is built
          </h2>
        </div>

        {/* Three input pills */}
        <div className="flex justify-center gap-3 mb-2 fade-up">
          <div className="px-4 py-2 rounded-full bg-white border border-zinc-200 text-sm font-medium text-zinc-700 shadow-sm">
            Diagnosis
          </div>
          <div className="px-4 py-2 rounded-full bg-white border border-zinc-200 text-sm font-medium text-zinc-700 shadow-sm">
            Treatment
          </div>
          <div className="px-4 py-2 rounded-full bg-white border border-zinc-200 text-sm font-medium text-zinc-700 shadow-sm">
            Insurer
          </div>
        </div>
        <p className="text-center text-zinc-400 text-xs mb-4 fade-up">You enter three things. PeerPrep does the rest.</p>

        {/* Roadmap SVG */}
        <div className="fade-up">
          <svg
            viewBox="0 0 700 400"
            className="w-full max-w-[700px] mx-auto"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Merge dot at top center */}
            <circle cx="350" cy="10" r="4" fill="#0d9488" opacity="0.4" />

            {/* Curved connectors */}
            <path d="M350 14 C 380 40, 520 30, 550 65" fill="none" stroke="#0d9488" strokeWidth="0.8" strokeDasharray="5 4" opacity="0.45" />
            <path d="M550 65 C 480 110, 220 90, 150 135" fill="none" stroke="#0d9488" strokeWidth="0.8" strokeDasharray="5 4" opacity="0.45" />
            <path d="M150 135 C 220 180, 490 160, 560 205" fill="none" stroke="#0d9488" strokeWidth="0.8" strokeDasharray="5 4" opacity="0.45" />
            <path d="M560 205 C 490 250, 250 230, 170 275" fill="none" stroke="#0d9488" strokeWidth="0.8" strokeDasharray="5 4" opacity="0.45" />
            <path d="M170 275 C 240 310, 420 300, 490 345" fill="none" stroke="#0d9488" strokeWidth="0.8" strokeDasharray="5 4" opacity="0.45" />

            {/* Node 1: Insurer identified (right) */}
            <circle cx="550" cy="65" r="52" fill="#0d9488" opacity="0.05" />
            <circle cx="550" cy="65" r="44" fill="white" stroke="#0d9488" strokeWidth="0.5" />
            <text x="550" y="60" textAnchor="middle" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }} fill="#0f766e">Insurer</text>
            <text x="550" y="78" textAnchor="middle" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }} fill="#0f766e">identified</text>
            <text x="610" y="68" textAnchor="start" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11 }} fill="#a1a1aa">100+ profiles</text>

            {/* Node 2: Guidelines matched (left) */}
            <circle cx="150" cy="135" r="52" fill="#0d9488" opacity="0.05" />
            <circle cx="150" cy="135" r="44" fill="white" stroke="#0d9488" strokeWidth="0.5" />
            <text x="150" y="130" textAnchor="middle" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }} fill="#0f766e">Guidelines</text>
            <text x="150" y="148" textAnchor="middle" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }} fill="#0f766e">matched</text>
            <text x="90" y="138" textAnchor="end" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11 }} fill="#a1a1aa">Specialty-specific</text>

            {/* Node 3: Evidence cited (right) */}
            <circle cx="560" cy="205" r="52" fill="#0d9488" opacity="0.05" />
            <circle cx="560" cy="205" r="44" fill="white" stroke="#0d9488" strokeWidth="0.5" />
            <text x="560" y="200" textAnchor="middle" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }} fill="#0f766e">Evidence</text>
            <text x="560" y="218" textAnchor="middle" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }} fill="#0f766e">cited</text>
            <text x="620" y="208" textAnchor="start" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11 }} fill="#a1a1aa">Peer-reviewed</text>

            {/* Node 4: Rebuttal built (left) */}
            <circle cx="170" cy="275" r="52" fill="#0d9488" opacity="0.05" />
            <circle cx="170" cy="275" r="44" fill="white" stroke="#0d9488" strokeWidth="0.5" />
            <text x="170" y="270" textAnchor="middle" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }} fill="#0f766e">Rebuttal</text>
            <text x="170" y="288" textAnchor="middle" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }} fill="#0f766e">built</text>
            <text x="110" y="278" textAnchor="end" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11 }} fill="#a1a1aa">Denial-specific</text>

            {/* Node 5: Your PA letter (destination — solid teal, slightly larger) */}
            <circle cx="490" cy="345" r="58" fill="#0d9488" opacity="0.08" />
            <circle cx="490" cy="345" r="46" fill="#0d9488" stroke="#0d9488" strokeWidth="0.5" />
            <text x="490" y="339" textAnchor="middle" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600 }} fill="white">Your PA</text>
            <text x="490" y="358" textAnchor="middle" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600 }} fill="white">letter</text>
            <text x="544" y="348" textAnchor="start" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11 }} fill="#a1a1aa">~30 seconds</text>
          </svg>

          <p className="text-center text-zinc-400 text-xs mt-1 fade-up">
            Not a generic template — mapped to your insurer's criteria.
          </p>
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════
// CALL SHIELD
// ════════════════════════════════════════════

function CallShieldSection() {
  const csSteps = [
    "Call auto-answered and reviewer held",
    "Push notification sent to your phone",
    "You're bridged in — transcription active",
    "Rebuttal mode surfaces counters live",
  ];
  return (
    <section className="py-24 px-8 max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      {/* Visual */}
      <div className="fade-up bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-2xl p-10 text-white relative overflow-hidden lg:order-none order-first">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(13,148,136,0.25)_0%,transparent_50%),radial-gradient(circle_at_80%_30%,rgba(20,184,166,0.15)_0%,transparent_50%)]" />
        <div className="relative z-10">
          <div className="cs-pulse bg-white/[0.08] border border-white/[0.12] rounded-xl px-5 py-4 flex items-center gap-4 mb-5">
            <div className="cs-ring w-11 h-11 bg-teal-600 rounded-full flex items-center justify-center shrink-0">
              <IconPhone />
            </div>
            <div>
              <div className="font-semibold text-sm">
                UnitedHealthcare — P2P Review
              </div>
              <div className="text-xs text-white/50">
                Incoming call to your Call Shield line
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            {csSteps.map((step) => (
              <div
                key={step}
                className="flex items-center gap-3 text-sm text-white/65"
              >
                <div className="w-[22px] h-[22px] bg-teal-500/20 rounded-full flex items-center justify-center text-teal-300 shrink-0">
                  <IconCheck />
                </div>
                {step}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Text */}
      <div className="fade-up">
        <div className="text-xs font-bold uppercase tracking-[0.12em] text-teal-600 mb-4">
          Call Shield
        </div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-5 leading-tight">
          Never miss a peer-to-peer call again
        </h2>
        <p className="text-zinc-500 text-base leading-relaxed mb-6">
          Insurance reviewers call once, often without warning. If you miss it,
          you lose weeks. Call Shield gives you a dedicated number that
          auto-answers, holds the reviewer, and pulls you into the call within
          seconds.
        </p>
        <ul className="flex flex-col gap-2.5 mb-8">
          {[
            "Dedicated phone line with auto-answer",
            "Push notification the moment a reviewer calls",
            "Seamless bridge into live call",
            "Real-time transcription from second one",
          ].map((item) => (
            <li
              key={item}
              className="flex items-baseline gap-2.5 text-sm text-zinc-900"
            >
              <span className="text-teal-600 font-bold text-xs">✓</span>
              {item}
            </li>
          ))}
        </ul>
        <span className="inline-flex items-center justify-center bg-zinc-200 text-zinc-500 px-6 py-3 rounded-lg text-sm font-semibold">
          Coming Soon
        </span>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════
// TRUST
// ════════════════════════════════════════════

function TrustSection() {
  return (
    <section className="bg-zinc-100 py-20 px-8 text-center">
      <div className="max-w-[800px] mx-auto fade-up">
        <div className="text-xs font-bold uppercase tracking-[0.12em] text-teal-600 mb-4">
          Privacy & Trust
        </div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-4">
          Patient PHI? Keep it.
        </h2>
        <p className="text-zinc-500 text-base leading-relaxed max-w-[600px] mx-auto mb-12">
          PeerPrep was built with a de-identified-by-design architecture.
          Patient information is scrubbed client-side before processing, and
          nothing identifiable is ever stored on our infrastructure.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="bg-white border border-zinc-200 rounded-xl p-7 text-left">
            <h4 className="text-sm font-bold text-zinc-900 mb-2">
              Client-Side PHI Scrubbing
            </h4>
            <p className="text-sm text-zinc-500 leading-relaxed">
              Identifying information is scrubbed on your device before
              processing. No patient data is stored on our servers.
            </p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl p-7 text-left">
            <h4 className="text-sm font-bold text-zinc-900 mb-2">
              HIPAA-Conscious Architecture
            </h4>
            <p className="text-sm text-zinc-500 leading-relaxed">
              Built to minimize PHI exposure by design, not by policy.
              Identifiable data is never persisted on our infrastructure.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════
// PRICING
// ════════════════════════════════════════════

function PricingSection() {
  return (
    <section className="py-24 px-8 max-w-[1100px] mx-auto" id="pricing">
      <div className="text-center mb-14 fade-up">
        <div className="text-xs font-bold uppercase tracking-[0.12em] text-teal-600 mb-4">
          Pricing
        </div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-2">
          Start free. Upgrade when you're ready.
        </h2>
        <p className="text-zinc-500 text-base">
          No credit card required to get started.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 fade-up">
        {/* Free */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-8 relative hover:-translate-y-1 hover:shadow-lg hover:shadow-black/[0.04] transition-all">
          <div className="text-base font-bold text-zinc-900 mb-2">Free</div>
          <div className="font-display text-5xl leading-none mb-1">
            $0<span className="text-base text-zinc-500 font-sans">/month</span>
          </div>
          <div className="text-sm text-zinc-500 mb-6">
            Try PeerPrep with real cases
          </div>
          <ul className="flex flex-col gap-2.5 mb-8">
            {[
              "2 PA letters per month",
              "Insurer criteria matching",
              "De-identified by design",
            ].map((f) => (
              <li key={f} className="flex items-baseline gap-2.5 text-sm">
                <span className="text-teal-600 font-bold text-xs">✓</span>
                {f}
              </li>
            ))}
          </ul>
          <Link
            to="/try?mode=prior-auth"
            className="w-full inline-flex items-center justify-center px-5 py-3 rounded-lg text-sm font-semibold border-2 border-zinc-200 text-zinc-900 hover:border-teal-600 hover:text-teal-600 transition-all no-underline"
          >
            Generate Free PA Letter
          </Link>
        </div>

        {/* Essential */}
        <div className="bg-white border-2 border-teal-600 rounded-2xl p-8 relative shadow-lg shadow-teal-600/10 hover:-translate-y-1 transition-all">
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal-600 text-white text-[11px] font-bold uppercase tracking-wider px-4 py-1 rounded-full">
            Most Popular
          </span>
          <div className="text-base font-bold text-zinc-900 mb-2">
            Essential
          </div>
          <div className="font-display text-5xl leading-none mb-1">
            $29<span className="text-base text-zinc-500 font-sans">/month</span>
          </div>
          <div className="text-sm text-zinc-500 mb-6">
            Unlimited PA letter generation
          </div>
          <ul className="flex flex-col gap-2.5 mb-8">
            {[
              "Unlimited PA letters",
              "Full insurer intelligence database",
              "Priority support",
            ].map((f) => (
              <li key={f} className="flex items-baseline gap-2.5 text-sm">
                <span className="text-teal-600 font-bold text-xs">✓</span>
                {f}
              </li>
            ))}
          </ul>
          <SignUpButton mode="redirect">
            <button className="w-full inline-flex items-center justify-center bg-teal-600 text-white px-5 py-3 rounded-lg text-sm font-semibold shadow-md shadow-teal-600/25 hover:bg-teal-700 transition-all cursor-pointer">
              Start with Essential
            </button>
          </SignUpButton>
        </div>

        {/* Pro — Coming Soon */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-8 relative opacity-75 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/[0.04] transition-all">
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-zinc-500 text-white text-[11px] font-bold uppercase tracking-wider px-4 py-1 rounded-full">
            Coming Soon
          </span>
          <div className="text-base font-bold text-zinc-900 mb-2">Pro</div>
          <div className="font-display text-5xl leading-none mb-1">
            $49<span className="text-base text-zinc-500 font-sans">/month</span>
          </div>
          <div className="text-sm text-zinc-500 mb-6">
            Full suite with Call Shield
          </div>
          <ul className="flex flex-col gap-2.5 mb-8">
            {[
              "Everything in Essential",
              "Call Shield auto-answer",
              "Live transcription",
              "Real-time rebuttal mode",
            ].map((f) => (
              <li key={f} className="flex items-baseline gap-2.5 text-sm">
                <span className="text-teal-600 font-bold text-xs">✓</span>
                {f}
              </li>
            ))}
          </ul>
          <span className="w-full inline-flex items-center justify-center bg-zinc-200 text-zinc-500 px-5 py-3 rounded-lg text-sm font-semibold">
            Coming Soon
          </span>
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════
// FINAL CTA
// ════════════════════════════════════════════

function FinalCTA() {
  return (
    <section className="bg-zinc-900 text-white py-20 px-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(13,148,136,0.2)_0%,transparent_60%)]" />
      <div className="max-w-[650px] mx-auto text-center relative z-10 fade-up">
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-4">
          Your patients deserve an advocate with equal firepower
        </h2>
        <p className="text-lg text-white/65 leading-relaxed mb-8">
          Every denied claim is a patient waiting. PeerPrep gives you the same
          clinical intelligence insurers use — so you can fight back with
          evidence, not frustration.
        </p>
        <Link
          to="/try?mode=prior-auth"
          className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-3.5 rounded-lg text-base font-semibold shadow-md shadow-teal-600/25 hover:bg-teal-700 hover:shadow-lg hover:shadow-teal-600/30 transition-all hover:-translate-y-px no-underline"
        >
          Generate Free Prior Auth Letter →
        </Link>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════
// FOOTER
// ════════════════════════════════════════════

function Footer() {
  return (
    <footer className="border-t border-zinc-200 max-w-[1200px] mx-auto px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-zinc-500">
      <span>© 2026 PeerPrep. Built by a physician, for physicians.</span>
      <div className="flex gap-6">
        <Link
          to="/privacy"
          className="text-zinc-500 hover:text-zinc-900 no-underline transition-colors"
        >
          Privacy
        </Link>
        <Link
          to="/terms"
          className="text-zinc-500 hover:text-zinc-900 no-underline transition-colors"
        >
          Terms
        </Link>
        <a
          href="mailto:support@peerprep.app"
          className="text-zinc-500 hover:text-zinc-900 no-underline transition-colors"
        >
          Contact
        </a>
      </div>
    </footer>
  );
}

// ════════════════════════════════════════════
// MAIN EXPORT
// ════════════════════════════════════════════

export default function LandingPage() {
  const wrapperRef = useFadeUp();

  return (
    <>
      <style>{styles}</style>
      <div
        ref={wrapperRef}
        className="landing-page bg-white text-zinc-900 overflow-x-hidden"
      >
        <Nav />
        <Hero />
        <ProblemSection />
        <FeaturesSection />
        <HowItWorks />
        <CallShieldSection />
        <TrustSection />
        <PricingSection />
        <FinalCTA />
        <Footer />
      </div>
    </>
  );
}